"use client";

import { useState, useRef, useEffect } from "react";
import { Upload, Play, Pause, Trash2, Star, Download, Loader2, Mic, Volume2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { VoiceRecorder } from "./VoiceRecorder";
import communicationService from "@/app/_module/services/communicationService";

interface VoicemailDrop {
  id: string;
  name: string;
  description?: string;
  audioUrl: string;
  audioSid?: string;
  duration: number;
  fileSize: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: Date;
}

export function VoicemailDropLibrary() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [voicemailDrops, setVoicemailDrops] = useState<VoicemailDrop[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showRecorder, setShowRecorder] = useState(false);
  const audioPlayersRef = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Load voicemail drops on mount
  useEffect(() => {
    loadVoicemailDrops();
    return () => {
      // Cleanup audio players on unmount
      audioPlayersRef.current.forEach(player => {
        player.pause();
        player.src = '';
        player.remove();
      });
    };
  }, []);

  const loadVoicemailDrops = async () => {
    try {
      setIsLoading(true);
      const response = await communicationService.getVoicemailDrops();
      const drops = response.data || [];
      setVoicemailDrops(drops.map((drop: any) => ({
        ...drop,
        createdAt: new Date(drop.createdAt),
      })));
    } catch (error) {
      toast({
        title: "Failed to Load",
        description: "Could not load voicemail drops. Please refresh the page.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("audio/")) {
      toast({
        title: "Invalid File Type",
        description: "Please upload an audio file (MP3, WAV, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Audio file must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    await uploadVoicemailDrop(file, file.name.replace(/\.[^/.]+$/, ""));
  };

  const uploadVoicemailDrop = async (file: File | Blob, name: string) => {
    setIsUploading(true);

    try {
      // Get audio duration
      const tempAudioUrl = URL.createObjectURL(file);
      const audio = new Audio(tempAudioUrl);
      await new Promise((resolve) => {
        audio.addEventListener("loadedmetadata", resolve);
      });
      const duration = Math.floor(audio.duration);
      URL.revokeObjectURL(tempAudioUrl);

      // Step 1: Upload audio file to get URL
      const uploadResult = await communicationService.uploadVoicemailDropAudio(file);

      // Step 2: Create voicemail drop with the uploaded URL
      await communicationService.createVoicemailDrop({
        name,
        audioUrl: uploadResult.audioUrl,
        duration,
        fileSize: file.size,
      });

      toast({
        title: "Voicemail Drop Uploaded",
        description: "Your voicemail drop has been uploaded successfully.",
      });

      // Reload list
      await loadVoicemailDrops();
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Failed to upload voicemail drop. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRecordedVoicemail = async (audioBlob: Blob, name: string) => {
    await uploadVoicemailDrop(audioBlob, name);
  };

  const handleSetDefault = async (id: string) => {
    try {
      await communicationService.setDefaultVoicemailDrop(id);

      // Update local state
      setVoicemailDrops(
        voicemailDrops.map((drop) => ({
          ...drop,
          isDefault: drop.id === id,
        }))
      );

      toast({
        title: "Default Voicemail Updated",
        description: "The selected voicemail drop has been set as default.",
      });
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to set default voicemail drop.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (id: string) => {
    const drop = voicemailDrops.find((d) => d.id === id);
    if (drop?.isDefault && voicemailDrops.length > 1) {
      toast({
        title: "Cannot Delete Default",
        description: "Please set another voicemail as default before deleting this one.",
        variant: "destructive",
      });
      return;
    }

    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      await communicationService.deleteVoicemailDrop(deleteConfirmId);

      // Stop audio if playing
      const player = audioPlayersRef.current.get(deleteConfirmId);
      if (player) {
        player.pause();
        player.src = '';
        player.remove();
        audioPlayersRef.current.delete(deleteConfirmId);
      }

      setVoicemailDrops(voicemailDrops.filter((drop) => drop.id !== deleteConfirmId));
      setDeleteConfirmId(null);

      toast({
        title: "Voicemail Drop Deleted",
        description: "The voicemail drop has been deleted.",
      });
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Failed to delete voicemail drop.",
        variant: "destructive",
      });
    }
  };

  const togglePlay = async (id: string, audioUrl: string) => {
    const currentPlayer = audioPlayersRef.current.get(id);

    if (playingId === id && currentPlayer) {
      currentPlayer.pause();
      setPlayingId(null);
    } else {
      // Stop all other players
      audioPlayersRef.current.forEach((player, playerId) => {
        if (playerId !== id) {
          player.pause();
        }
      });

      if (!currentPlayer) {
        try {
          // Fetch audio as blob to avoid cross-origin issues
          const response = await fetch(audioUrl);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          const newPlayer = document.createElement('audio');
          newPlayer.style.display = 'none';
          newPlayer.volume = 1.0;
          document.body.appendChild(newPlayer);
          newPlayer.src = blobUrl;
          newPlayer.addEventListener('ended', () => {
            setPlayingId(null);
          });
          newPlayer.addEventListener('error', () => {
            toast({
              title: "Playback Error",
              description: "Failed to play audio file. Try re-uploading as MP3 or WAV.",
              variant: "destructive",
            });
            setPlayingId(null);
          });
          audioPlayersRef.current.set(id, newPlayer);
          await newPlayer.play();
          setPlayingId(id);
        } catch (err) {
          toast({
            title: "Playback Error",
            description: "Could not load audio file.",
            variant: "destructive",
          });
        }
      } else {
        currentPlayer.currentTime = 0;
        currentPlayer.play().catch(() => {});
        setPlayingId(id);
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-b from-white to-slate-50/80">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 shadow-md shadow-indigo-200">
              <Volume2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold tracking-tight">Voicemail Drop Library</CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Pre-recorded messages for automated voicemail drops
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 pt-0">
          {/* Upload Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="group relative flex items-center gap-3 p-4 rounded-xl border border-dashed border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30 transition-all duration-200 text-left"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-slate-100 group-hover:bg-indigo-100 transition-colors">
                {isUploading ? (
                  <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                ) : (
                  <Upload className="h-5 w-5 text-slate-500 group-hover:text-indigo-500 transition-colors" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">{isUploading ? "Uploading..." : "Upload File"}</p>
                <p className="text-xs text-slate-400">MP3, WAV up to 5MB</p>
              </div>
            </button>

            <button
              onClick={() => setShowRecorder(!showRecorder)}
              className={`group relative flex items-center gap-3 p-4 rounded-xl border border-dashed transition-all duration-200 text-left ${
                showRecorder
                  ? "border-red-300 bg-red-50/40"
                  : "border-slate-200 bg-white hover:border-red-300 hover:bg-red-50/30"
              }`}
            >
              <div className={`flex items-center justify-center h-10 w-10 rounded-lg transition-colors ${
                showRecorder
                  ? "bg-red-100"
                  : "bg-slate-100 group-hover:bg-red-100"
              }`}>
                <Mic className={`h-5 w-5 transition-colors ${
                  showRecorder
                    ? "text-red-500"
                    : "text-slate-500 group-hover:text-red-500"
                }`} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">{showRecorder ? "Recording..." : "Record Message"}</p>
                <p className="text-xs text-slate-400">Up to 2 minutes</p>
              </div>
            </button>
          </div>

          {/* Inline Voice Recorder */}
          {showRecorder && (
            <VoiceRecorder onSave={handleRecordedVoicemail} onClose={() => setShowRecorder(false)} />
          )}

          {/* Voicemail Drops List */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <div className="h-12 w-12 rounded-full border-2 border-indigo-100" />
                <Loader2 className="h-12 w-12 absolute inset-0 animate-spin text-indigo-500" />
              </div>
              <p className="text-sm text-slate-400 mt-3">Loading library...</p>
            </div>
          ) : voicemailDrops.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                <Mic className="h-7 w-7 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-500">No voicemail drops yet</p>
              <p className="text-xs text-slate-400 mt-1 text-center">Upload an audio file or record a new message to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {voicemailDrops.map((drop) => (
                <div
                  key={drop.id}
                  className={`group relative flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${
                    drop.isDefault
                      ? "bg-gradient-to-r from-indigo-50/80 to-blue-50/50 border border-indigo-100 shadow-sm"
                      : "bg-white border border-slate-100 hover:border-slate-200 hover:shadow-sm"
                  } ${playingId === drop.id ? "ring-2 ring-indigo-200 ring-offset-1" : ""}`}
                >
                  {/* Play Button */}
                  <button
                    onClick={() => togglePlay(drop.id, drop.audioUrl)}
                    className={`relative flex items-center justify-center h-11 w-11 rounded-full shrink-0 transition-all duration-200 ${
                      playingId === drop.id
                        ? "bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow-lg shadow-indigo-200 scale-105"
                        : "bg-slate-100 text-slate-500 hover:bg-gradient-to-br hover:from-indigo-500 hover:to-blue-600 hover:text-white hover:shadow-md hover:shadow-indigo-200 hover:scale-105"
                    }`}
                  >
                    {playingId === drop.id ? (
                      <Pause className="h-4 w-4 ml-0" />
                    ) : (
                      <Play className="h-4 w-4 ml-0.5" />
                    )}
                    {playingId === drop.id && (
                      <span className="absolute inset-0 rounded-full animate-ping bg-indigo-400 opacity-20" />
                    )}
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800 truncate">{drop.name}</p>
                      {drop.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-indigo-100 text-indigo-600">
                          <Star className="h-2.5 w-2.5 fill-current" />
                          Default
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-mono text-slate-400">{formatDuration(drop.duration)}</span>
                      <span className="text-slate-200">·</span>
                      <span className="text-xs text-slate-400">{formatFileSize(drop.fileSize)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    {!drop.isDefault && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50"
                        onClick={() => handleSetDefault(drop.id)}
                        title="Set as default"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      onClick={() => window.open(drop.audioUrl, "_blank")}
                      title="Download"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(drop.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Default star indicator (always visible) */}
                  {drop.isDefault && (
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400 shrink-0 group-hover:hidden" />
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voicemail Drop?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the voicemail drop from
              your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>
    </>
  );
}
